import { useGT } from 'gt-react';
import { defineComponentType } from '@/components/report-canvas/registry';
import type { ComponentRegistry } from '@/components/report-canvas/registry';
import type { BaseComponentConfig } from '@/components/report-canvas/types';
import { ConfigRow } from '@/components/report-canvas/charts/shared';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ARAgingChart } from '@/components/accounts-receivable/ARAgingChart';
import { formatCurrency } from '@/components/common/Currency';
import { parseMoney, toMoneyNumber } from '@erp/shared-utils';
import { useARAnalytics } from './ARAnalyticsContext';

// ── AR aging chart widget ─────────────────────────────────────────────────────
// Wraps the existing ARAgingChart (bar/stacked/pie toggle built in) so the
// canvas default layout reproduces today's pages exactly.

interface ArAgingChartWidgetConfig extends BaseComponentConfig {
  type: 'ar-aging-chart';
}

function ArAgingChartRenderer() {
  const { aging } = useARAnalytics();
  return (
    <div className="min-h-0 flex-1 overflow-auto">
      <ARAgingChart
        totals={aging.totals}
        customers={aging.customers}
        bucketDefinitions={aging.bucketDefinitions}
        isLoading={aging.isLoading}
      />
    </div>
  );
}

function ArAgingChartPreview() {
  return (
    <div className="flex h-24 items-center justify-center text-foreground">
      <svg viewBox="0 0 44 36" className="h-16 w-auto" fill="none">
        <line
          x1="7"
          y1="30"
          x2="40"
          y2="30"
          stroke="currentColor"
          strokeWidth="1"
          strokeOpacity="0.3"
        />
        <rect
          x="10"
          y="20"
          width="6"
          height="10"
          rx="1"
          fill="currentColor"
          fillOpacity="0.9"
        />
        <rect
          x="19"
          y="14"
          width="6"
          height="16"
          rx="1"
          fill="currentColor"
          fillOpacity="0.65"
        />
        <rect
          x="28"
          y="9"
          width="6"
          height="21"
          rx="1"
          fill="currentColor"
          fillOpacity="0.45"
        />
        <rect
          x="37"
          y="17"
          width="6"
          height="13"
          rx="1"
          fill="currentColor"
          fillOpacity="0.25"
        />
      </svg>
    </div>
  );
}

const arAgingChartDefinition = defineComponentType<ArAgingChartWidgetConfig>({
  type: 'ar-aging-chart',
  label: 'Aging Overview',
  description:
    'Self-contained AR aging overview with its own bar/stacked/pie toggle. Ignores datasets — never set datasetKey. No config.',
  version: 1,
  size: { w: 12, h: 8, minW: 6, minH: 6 },
  make: (id) => ({ type: 'ar-aging-chart', id }),
  Renderer: ArAgingChartRenderer,
  // The chart carries its own bar/stacked/pie toggle; nothing to configure.
  ConfigForm: null,
  Preview: ArAgingChartPreview,
});

// ── AR metric tile widget ─────────────────────────────────────────────────────

const AR_METRICS = [
  'total-outstanding',
  'overdue',
  'incoming-payments',
  'open-invoices',
  'dso',
] as const;
type ArMetric = (typeof AR_METRICS)[number];

interface ArMetricTileConfig extends BaseComponentConfig {
  type: 'ar-metric-tile';
  metric: ArMetric;
}

function formatMoney(value: string | null | undefined): string {
  return formatCurrency(toMoneyNumber(parseMoney(value ?? '0')), {
    currency: 'USD',
  });
}

function useMetricLabels(): Record<ArMetric, string> {
  const gt = useGT();
  return {
    'total-outstanding': gt('Total Open AR'),
    overdue: gt('Overdue'),
    'incoming-payments': gt('Incoming Payments'),
    'open-invoices': gt('Open Invoices'),
    dso: gt('DSO'),
  };
}

function ArMetricTileRenderer({
  component,
}: {
  component: ArMetricTileConfig;
}) {
  const { summary, profile } = useARAnalytics();
  const labels = useMetricLabels();

  // Customer surface reads the profile bundle; org surface the summary.
  const value = ((): string => {
    switch (component.metric) {
      case 'total-outstanding':
        if (profile) return formatMoney(profile.aging.totalOpen);
        if (summary) return formatMoney(summary.totalOutstanding);
        return '—';
      case 'overdue':
        if (profile) return formatMoney(profile.aging.totalOverdue);
        if (summary) return formatMoney(summary.overdueAmount);
        return '—';
      case 'incoming-payments':
        if (profile) return formatMoney(profile.incomingPayments.total);
        if (summary) return formatMoney(summary.incomingPaymentsTotal);
        return '—';
      case 'open-invoices':
        if (profile) return String(profile.aging.invoiceCount);
        if (summary) return String(summary.openInvoiceCount);
        return '—';
      case 'dso':
        if (summary?.dso != null) return `${Math.round(summary.dso)}d`;
        return '—';
    }
  })();

  return (
    <div className="flex min-h-0 flex-1 flex-col items-start justify-center gap-1 p-4">
      <p className="text-sm font-medium text-muted-foreground">
        {labels[component.metric]}
      </p>
      <p className="text-2xl font-bold">{value}</p>
    </div>
  );
}

function ArMetricTileConfigForm({
  component,
  onUpdate,
}: {
  component: ArMetricTileConfig;
  onUpdate: (updated: ArMetricTileConfig) => void;
}) {
  const gt = useGT();
  const labels = useMetricLabels();
  return (
    <div className="space-y-4">
      <ConfigRow label={gt('Metric')}>
        <Select
          value={component.metric}
          onValueChange={(v) => {
            const metric = AR_METRICS.find((m) => m === v);
            if (metric) onUpdate({ ...component, metric });
          }}
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {AR_METRICS.map((m) => (
              <SelectItem key={m} value={m}>
                {labels[m]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </ConfigRow>
    </div>
  );
}

function ArMetricTilePreview() {
  return (
    <div className="flex h-24 flex-col items-center justify-center gap-1 text-foreground">
      <svg viewBox="0 0 44 36" className="h-16 w-auto" fill="none">
        <rect
          x="4"
          y="6"
          width="36"
          height="24"
          rx="3"
          stroke="currentColor"
          strokeWidth="1"
          strokeOpacity="0.3"
        />
        <rect
          x="9"
          y="12"
          width="14"
          height="2.5"
          rx="1.25"
          fill="currentColor"
          fillOpacity="0.35"
        />
        <rect
          x="9"
          y="19"
          width="22"
          height="5"
          rx="2"
          fill="currentColor"
          fillOpacity="0.85"
        />
      </svg>
    </div>
  );
}

const arMetricTileDefinition = defineComponentType<ArMetricTileConfig>({
  type: 'ar-metric-tile',
  label: 'Metric Tile',
  description:
    'Self-contained KPI tile showing one AR metric. Ignores datasets — never set datasetKey. Config "metric" must be one of: total-outstanding | overdue | incoming-payments | open-invoices | dso.',
  version: 1,
  size: { w: 3, h: 2, minW: 2, minH: 2 },
  make: (id) => ({ type: 'ar-metric-tile', id, metric: 'total-outstanding' }),
  Renderer: ArMetricTileRenderer,
  ConfigForm: ArMetricTileConfigForm,
  Preview: ArMetricTilePreview,
});

// ── Registry fragment ─────────────────────────────────────────────────────────

export const arWidgetRegistry: ComponentRegistry = {
  [arAgingChartDefinition.type]: arAgingChartDefinition,
  [arMetricTileDefinition.type]: arMetricTileDefinition,
};
